import React, { useState } from 'react';
import { IPermanentMedicament } from '../PermanentMedication/PernamentMedicationItem';
import apiClient from '../../../../Services/ApiClient';
import { useLazyPatientProfileDataQuery } from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import { formatQueryString } from '../../../../Services/Api/FormatQueryString';
import useApiClient from '../../../Base/Hooks/useApiClient';
import Loading from '../../../../Components/Loading';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
// @ts-ignore
import styles from './CheckLastIssueOfMedication.module.scss';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import useTranslation from '../../../../Hooks/useTranslation';

interface Props {
	medications: IPermanentMedicament[];
	patientId: string;
	onIssue: (medications: IPermanentMedicament[]) => void;
}

interface HistoryItem {
	item: IPermanentMedicament;
	lastIssueDate?: string;
}

const CheckLastIssueOfMedication: React.FC<Props> = ({
	medications,
	patientId,
	onIssue,
}) => {
	const [getPatientProfile] = useLazyPatientProfileDataQuery();
	const { displayError } = useApiClient();
	const { t } = useTranslation();
	const [history, setHistory] = useState<HistoryItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedItems, setSelectedItems] = useState<IPermanentMedicament[]>(
		[],
	);

	const getPatientData = async () => {
		const data = await getPatientProfile({ userId: patientId });
		const userProfile = data?.data?.user?.profile;
		const userAddress = userProfile?.address;
		if (userProfile && userAddress) {
			return {
				firstName: userProfile.firstName,
				lastName: userProfile.lastName,
				dateOfBirth: userProfile.birthDate,
				months: 60,
				atc: 5,
				street: userAddress.street,
				descriptiveNumber: userAddress.descriptiveNumber,
				guideNumber: userAddress.guideNumber,
				city: userAddress.city,
				postCode: userAddress.postCode,
				version: 2,
			};
		}
		return undefined;
	};

	const loadMedicalRecord = async () => {
		const patientData = await getPatientData();
		if (patientData) {
			try {
				const query = formatQueryString(patientData);
				const {
					data: { data },
				} = await apiClient.get('/erecept/lekovy-zaznam?' + query, {
					catchError: false,
				});
				if (data) {
					const medicationHistory: { date: string; name: string }[] = [];
					data.forEach(
						(item: { children: { title: string }[]; title: string }) => {
							item.children.forEach(i => {
								medicationHistory.push({ date: item.title, name: i.title });
							});
						},
					);
					return medicationHistory;
				}
				return undefined;
			} catch (e: any) {
				displayError(e);
			}
		}
		return undefined;
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		const history = await loadMedicalRecord();
		const temp: HistoryItem[] = [];
		if (history) {
			medications.forEach(item => {
				const name = 'item' in item.data ? item.data.item.name : item.data.name;
				const match = history.find(h => h.name === name);
				temp.push({
					item,
					lastIssueDate: match?.date,
				});
			});
		}
		setHistory(temp);
		setIsLoading(false);
	}, []);

	const handleOnCheck = (item: IPermanentMedicament) => {
		if (selectedItems.some(i => i.id === item.id)) {
			setSelectedItems(prev => prev.filter(i => i.id !== item.id));
		} else {
			setSelectedItems(prev => [...prev, item]);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<table>
				<tbody>
					{history.map(historyItem => {
						const data = historyItem.item.data;
						const name = 'item' in data ? data.item.name : data.name;
						const addition = 'item' in data ? data.item.addition : undefined;
						return (
							<tr key={historyItem.item.id}>
								<td>
									<div className={styles.name}>
										{name} {addition && <span>{addition}</span>}
									</div>
								</td>
								<td>
									{historyItem.lastIssueDate && (
										<span>
											{t('component.checkLastIssueOfMedication.lastIssue')}:{' '}
											{historyItem.lastIssueDate}
										</span>
									)}
								</td>
								<td>
									<div className={styles.checkboxWrapper}>
										<Checkbox
											tx='component.checkLastIssueOfMedication.issue'
											onChangeExtra={() => handleOnCheck(historyItem.item)}
											value={selectedItems.some(
												i => i.id === historyItem.item.id,
											)}
										/>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div className='mt-3'>
				<Button
					onClick={() => onIssue(selectedItems)}
					className={styles.submitButton}
					theme={Theme.GREEN}
					title='component.checkLastIssueOfMedication.submitButton'
				/>
			</div>
		</div>
	);
};

export default CheckLastIssueOfMedication;
